import {
  AccessibilityCategory,
  AccessibilityCategoryHybrid,
  AccessibilityFilters,
  AccessibilityTag,
  AddPhotoImageData,
  Business,
  BusinessNote,
  BusinessNoteAction,
  BusinessNoteTask,
  BusinessPhoto,
  BusinessPhotoFirst,
  businessNotesAll,
  ComplexCategoriesData,
  OpeningHour,
  PhotoAltTextTemplate,
  ReviewPhoto,
  SearchApiParams,
  VenueAccessibilityExperienceStats,
  VenueDetails,
  VenueReview,
} from "../types";
import { apiServer } from "common/config";
import { fetchWithToken } from "./fetchWithToken";
import { resolveGuardrail } from "./photoGuardrail";
import { resolveBrandFields } from "../utils/resolveBrandFields";

export class NotFoundError extends Error {
  constructor(slug: string) {
    super(`Venue not found: ${slug}`);
    this.name = "NotFoundError";
  }
}

/**
 * Thrown by loadLocationDetails (only when opted in via throwOnSlugRedirect) when a
 * requested slug is a retired slug and the backend permanently-redirected the fetch
 * to the venue's current canonical slug. The SSR venue route catches this and re-emits
 * a page-level HTTP 301 so crawlers/browsers land on the canonical URL.
 */
export class SlugRedirectError extends Error {
  constructor(public canonicalSlug: string) {
    super("slug_redirect");
    this.name = "SlugRedirectError";
  }
}

export async function syncAccessibility(
  businessId: number,
  slug: string,
): Promise<AccessibilityCategory[]> {
  const formData = new FormData();
  formData.append("business_id", businessId.toString());
  formData.append("slug", slug);

  const response = await fetch(
    `${apiServer + "/business/sync-accessibility"}`,
    {
      method: "POST",
      body: formData,
      headers: {
        Accept: "application/json",
      },
    },
  );
  const data = await response.json();
  if (data.status === "error") {
    throw Error(data.message);
  } else {
    return mapAccessibilityTag(data.data.review_tags);
  }
}

export async function syncAccessibilityHybrid(
  businessId: number,
  slug: string,
): Promise<AccessibilityCategoryHybrid[]> {
  const formData = new FormData();
  formData.append("business_id", businessId.toString());
  formData.append("slug", slug);
  console.log(formData);
  const response = await fetch(
    `${apiServer + "/business/sync-accessibility"}`,
    {
      method: "POST",
      body: formData,
      headers: {
        Accept: "application/json",
      },
    },
  );
  const data = await response.json();
  if (data.status === "error") {
    throw Error(data.message);
  } else {
    return mapHybridAccessibilityTag(data.data.review_tags);
  }
}

type VenueDetailsParams = {
  latitude: string;
  longitude: string;
  token: string;
  type?: "Basic" | "Complex";
  /**
   * Opt-in (default off). When set, a fetch that followed a backend 301 for a retired
   * slug throws SlugRedirectError with the current canonical slug instead of returning
   * data. Only the web SSR venue route enables this — widgets/native leave it off so
   * their shared usage is unchanged.
   */
  throwOnSlugRedirect?: boolean;
};

export async function loadLocationDetails(
  slug: string,
  params: VenueDetailsParams,
): Promise<VenueDetails> {
  const queryString = new URLSearchParams({
    lat: params.latitude,
    long: params.longitude,
    type: params.type || "Basic",
  }).toString();

  const urlWithParams = `${
    apiServer + "/business/details/" + slug
  }?${queryString}`;

  const response = await fetch(urlWithParams, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization:
        params.token && params.token.length > 0 ? `Bearer ${params.token}` : "",
    },
  });

  if (response.status === 404) {
    throw new NotFoundError(slug);
  }

  const data = await response.json();

  if (data.status === "error") {
    if (data.message?.toLowerCase().includes("not found")) {
      throw new NotFoundError(slug);
    }
    throw Error(data.message);
  } else {
    const raw = data.data;

    // Retired-slug 301: the backend permanently-redirects an old slug to the current
    // canonical URL; fetch follows it (default redirect: "follow"), so
    // response.redirected is the unambiguous signal — a live cohort-alias slug returns
    // a plain 200 (redirected === false) and is untouched. Guard on a real, different
    // canonical slug so an infra-level redirect (http→https, host/trailing-slash
    // normalization) can never trigger a self-301 loop.
    if (params.throwOnSlugRedirect && response.redirected) {
      const canonicalSlug: string | undefined = raw?.business?.slug;
      if (canonicalSlug && canonicalSlug !== slug) {
        throw new SlugRedirectError(canonicalSlug);
      }
    }

    return mapJsonToLocationDetails(raw);
  }
}

export function mapJsonToLocationDetails(json: any): VenueDetails {
  const brand = resolveBrandFields(json);
  return {
    id: json.id,
    placeId: json.place_id,
    name: json.name,
    phone: json.phone,
    website: json.website,
    description: json.description,
    address: json.address,
    latitude: json.latitude,
    longitude: json.longitude,
    rating: json.rating,
    types: json.types,
    keepUpdated: json.keep_updated,
    latestReviewDateUpdated: json.latest_review_date_updated,
    businessStatus: json.business_status,
    what3Words: json.what3words,
    businessVerified: json.business_verified,
    businessVerifiedDatetime: json.business_verified_datetime,
    openNow: json.open_now,
    reviewDetailsSubmitted: json.review_details_submitted,
    hasExperiences: json.has_experiences,
    hasAlerts: json.has_alerts,
    hasTeamTags: json.has_team_tags,
    hasGeneralUserTags: json.has_general_user_tags,
    venueDataSource: json.venue_data_source,
    hasGeneralUserPhotos: json.has_general_user_photos,
    tagIds: json.tag_ids,
    detailsFetchedAt: json.details_fetched_at,
    placeidCheckedAt: json.placeid_checked_at,
    googlePhotosCount: json.google_photos_count,
    sociabilityVisible: json.sociability_visible,
    cityLocation: json.city_location,
    route: json.route ?? undefined,
    locality: json.locality ?? undefined,
    sublocality: json.sublocality ?? undefined,
    postalTown: json.postal_town ?? undefined,
    business: mapBusiness(json.business, brand.brandId),
    allLikesData: json.allLikesData,
    brandName: brand.brandName,
    brandId: brand.brandId,
    likesCount: json.likesCount,
    dislikesCount: json.dislikesCount,
    disabilityTrainedStaff: json.disabilityTrainedStaff,
    businessPhotoCount: json.business_photo_count,
    businessPhotoFirst: mapBusinessPhotoFirst(json.business_photo_first),
    businessNotices: json.business_notices,
    googleReviewsCount: json.google_reviews_count,
    openingHours: mapOpeningHours(json.opening_hours),
    businessBadges: json.businessBadges,
    populartimeCount: json.populartimeCount,
    popularTimeData: json.popularTimeData,
    businessAccessibilityVerifiedBadge: json.businessAccessibilityVerifiedBadge,
    businessClaimData: json.businessClaims,
    businessInterestData: json.businessInterests,
    businessType: json.businessType,
    tagAppliedOptions: json.tag_applied_options,
    businessPhotos: json.business_photos?.map(mapBusinessPhoto) || [],
    templateTypeCategoriesCount: json.templateTypeCategoriesCount,
    distance: json.distance,
    showBusinessNotesDataForMapper: json.showBusinessNotesDataForMapper,
    businessNotesForMappers: json.businessNotesForMappers,
    enableSendToMobile: json.enable_send_to_mobile,
    photoAltTextTemplate: mapPhotoAltTextTemplate(json.photoAltTextTemplate),
    templateType: json.template_type,
    isPublicTemplate: json.is_public_template,
    businessReviews: mapVenueReviews(json.business_reviews),
    businessSpaceReviews: json.business_space_reviews,
    nearbyBusinessCategories: json.nearbyBusinessCategories,
    filterAccessibility: json.filter_accessibility,
    nearbyBusinessCategoriesCount: json.nearbyBusinessCategoriesCount,
    isStopSearchEngine: json.is_stop_search_engine,
    businessSavedByUser: json.businessSavedByUser,
    isNoteStatusPending: json.isNoteStatusPending,
    isReturnToVenue: json.isReturnToVenue,
    businessNotesAll: json.businessNotesAll,
    businessVenueNotesAreaLocations: json.businessVenueNotesAreaLocations,
    businessNotesActionsVenueNotes: json.businessNotesActionsVenueNotes,
    businessNoteHeadingSuggestions: json.businessNoteHeadingSuggestions,
    businessNotesActions: json.businessNotesActions,
    businessSociabilitySaysPending: json.businessSociabilitySaysPending,
    siteMapPhoto: json.site_map_photo,
    transportOptionImage: json.transport_option_image,
    transportOptionText: json.transport_option_text,
    hasNearbyVenues: json.hasNearbyVenues,
    businessInterestsTypes: json.businessInterestsTypes,
    businessTabs: json.businessTabs || [],
    cohortsData: json.cohortsData || [],
    businessSlugDetails: json.businessSlugDetails ?? undefined,
    city: json.city,
    userDistance: json.user_distance,
    publicUrl: json.public_url || json.publicUrl,
    isComplex: json.is_complex,
    venueAccessibilityExperienceStats: mapVenueAccessibilityExperienceStats(
      json.business_reviews?.accessibility_experience_stats,
    ),
    openFlagsCount: json.open_flags_count,
    to_be_verified: json.to_be_verified,
  };
}

function mapOpeningHours(openingHours: any): OpeningHour[] {
  //{"close_time": 2300, "close_time_utc": 2300, "created_at": "2023-11-27 07:33:42", "day": 0, "google_business_id": "f2cc05dc37b267d588dd8fe890bb3fe73a8bb2b4", "id": 2282936, "mode": 0, "open_time": 700, "open_time_utc": 700, "updated_at": "2023-11-27 07:33:42", "utc_offset": 0}
  return (
    openingHours?.map((openingHour: any) => ({
      closeTime: openingHour.close_time,
      closeTimeUtc: openingHour.close_time_utc,
      createdAt: openingHour.created_at,
      day: openingHour.day,
      googleBusinessId: openingHour.google_business_id,
      id: openingHour.id,
      mode: openingHour.mode,
      openTime: openingHour.open_time,
      openTimeUtc: openingHour.open_time_utc,
      updatedAt: openingHour.updated_at,
      utcOffset: openingHour.utc_offset,
    })) || []
  );
}
function mapBusiness(business: any, brandId: any = null): Business {
  const resolved = resolveBrandFields({ brandId, ...business });
  return {
    id: business.id,
    googleBusinessId: business.google_business_id,
    deletedAt: business.deleted_at,
    createdAt: business.created_at,
    updatedAt: business.updated_at,
    foursquareId: business.foursquare_id,
    foursquareUpdatedAt: business.foursquare_updated_at,
    userId: business.user_id,
    // Prefer venue-level brand (passed in), then any brand on the nested business.
    brandId: resolved.brandId || null,
    slug: business.slug,
    venueTypeId: business.venue_type_id,
    templateId: business.template_id, // if template id is 1 or null basic otherwise complex
    description: business.description,
    subDescription: business.sub_description,
    allowSpaceFlagging: business.allow_space_flagging,
    allowSpaceReview: business.allow_space_review,
    themeName: business.theme_name,
    themeLogo: business.theme_logo,
    themeLogoAltText: business.theme_logo_alt_text,
    themeLogoFocalX:
      business.theme_logo_focal_x != null
        ? Number(business.theme_logo_focal_x)
        : null,
    themeLogoFocalY:
      business.theme_logo_focal_y != null
        ? Number(business.theme_logo_focal_y)
        : null,
    coverPhoto:
      typeof business.cover_photo === "string" ? business.cover_photo : null,
    coverPhotoAltText:
      typeof business.cover_photo_alt_text === "string"
        ? business.cover_photo_alt_text
        : null,
    coverPhotoFocalX:
      business.cover_photo_focal_x != null
        ? Number(business.cover_photo_focal_x)
        : null,
    coverPhotoFocalY:
      business.cover_photo_focal_y != null
        ? Number(business.cover_photo_focal_y)
        : null,
    themeEmail: business.theme_email,
    themeDownloadAppLink: business.theme_download_app_link,
    qrCode: business.qr_code,
    businessType: business.business_type,
    imageExistChecked: business.image_exist_checked,
    isPasswordProtect: business.is_password_protect,
    isStopSearchEngine: business.is_stop_search_engine,
    siteMapPhoto: business.site_map_photo,
    externalLinkTitle: business.external_link_title,
    externalLinkUrl: business.external_link_url,
    isSsoProtect: business.is_sso_protect,
    ssoTenantId: business.sso_tenant_id,
    hqChecked: business.hq_checked,
    hqCheckedByUserId: business.hq_checked_by_user_id,
    hqCheckedAt: business.hq_checked_at,
    completeness: business.completeness,
    missingAreas: business.missing_areas,
    complexCacheUpdatedAt: business.complex_cache_updated_at,
    transportOptionImage: business.transport_option_image,
    transportOptionText: business.transport_option_text,
    publicUrl: business.public_url || business.publicUrl,
    venue_summary: business.venue_summary,
    venue_space_summaries: business.venue_space_summaries ?? null,
    isComplex: business.is_complex,
    dashboard_urls: business.dashboard_urls ?? null,
  };
}

function mapBusinessPhotoFirst(businessPhotoFirst: any): BusinessPhotoFirst {
  return {
    businessId: businessPhotoFirst.business_id,
    reviewPhotosId: businessPhotoFirst.review_photos_id,
    userId: businessPhotoFirst.user_id,
    path: businessPhotoFirst.path,
    reviewId: businessPhotoFirst.review_id,
    reviewDetailId: businessPhotoFirst.review_detail_id,
    typeId: businessPhotoFirst.type_id,
    altText: businessPhotoFirst.alt_text,
    tagApplied: businessPhotoFirst.tag_applied,
    reviewLevelId: businessPhotoFirst.review_level_id,
    coverPhoto: businessPhotoFirst.cover_photo,
    createdAt: businessPhotoFirst.created_at,
    sortOrder: businessPhotoFirst.sort_order,
    userName: businessPhotoFirst.user_name,
    reviewLevel: businessPhotoFirst.review_level,
    photoType: businessPhotoFirst.photo_type,
    tagCategories: businessPhotoFirst.tag_categories,
    tagAppliedForOrderby: businessPhotoFirst.tag_applied_for_orderby,
  };
}

function mapBusinessPhoto(businessPhoto: any): BusinessPhoto {
  return {
    businessId: businessPhoto.business_id,
    reviewPhotosId: businessPhoto.review_photos_id,
    userId: businessPhoto.user_id,
    path: businessPhoto.path,
    reviewId: businessPhoto.review_id,
    reviewDetailId: businessPhoto.review_detail_id,
    typeId: businessPhoto.type_id,
    altText: businessPhoto.alt_text,
    tagApplied: businessPhoto.tag_applied,
    reviewLevelId: businessPhoto.review_level_id,
    coverPhoto: businessPhoto.cover_photo,
    createdAt: businessPhoto.created_at,
    sortOrder: businessPhoto.sort_order,
    userName: businessPhoto.user_name,
    reviewLevel: businessPhoto.review_level,
    photoType: businessPhoto.photo_type,
    tagCategories: businessPhoto.tag_categories,
    tagAppliedForOrderby: businessPhoto.tag_applied_for_orderby,
    tagName: businessPhoto.tag_name,
    width: businessPhoto.width,
    height: businessPhoto.height,
    user_roles: businessPhoto.user_roles,
  };
}

function mapPhotoAltTextTemplate(
  photoAltTextTemplate: any,
): PhotoAltTextTemplate {
  return {
    photoAltTextTemplate: {
      "Entrances & Exits": photoAltTextTemplate["Entrances & Exits"],
      "Indoor Areas": photoAltTextTemplate["Indoor Areas"],
      "Outdoor Areas": photoAltTextTemplate["Outdoor Areas"],
      Bathroom: photoAltTextTemplate["Bathroom"],
      "Visual, hearing & sensory":
        photoAltTextTemplate["Visual, hearing & sensory"],
      "Fitting Rooms": photoAltTextTemplate["Fitting Rooms"],
      "Lifts & Stairs": photoAltTextTemplate["Lifts & Stairs"],
      "Bathroom Facilities": photoAltTextTemplate["Bathroom Facilities"],
    },
  };
}

/** Normalizes review photo rows from `sync-accessibility` (snake_case / mixed). */
export function mapReviewPhotoFromApi(e: any): ReviewPhoto {
  const pathMain = e.path ?? "";
  const pathThumbRaw = e.path_thumb ?? e.pathThumb ?? "";
  const path = pathMain || pathThumbRaw;
  const pathThumb = pathThumbRaw || pathMain;
  const userName = e.user_name ?? e.userName;
  const altTextGeneratedBy =
    e.alt_text_generated_by ?? e.altTextGeneratedBy ?? null;
  const aiGeneratedAltText =
    e.ai_generated_alt_text ?? e.aiGeneratedAltText ?? null;
  const imageUpdatedAt = e.image_updated_at ?? e.imageUpdatedAt ?? null;
  const othersUpdatedAt = e.others_updated_at ?? e.othersUpdatedAt ?? null;
  const widthThumb = e.width_thumb ?? e.widthThumb;
  const heightThumb = e.height_thumb ?? e.heightThumb;

  return {
    id: e.id,
    review_photos_id: e.review_photos_id,
    userId: e.user_id ?? e.userId ?? 0,
    userName,
    businessId: e.business_id ?? e.businessId ?? 0,
    path,
    pathThumb,
    typeId: e.type_id ?? e.typeId ?? 0,
    reviewId: e.review_id ?? e.reviewId ?? 0,
    createdAt: e.created_at ?? e.createdAt ?? "",
    updatedAt: e.updated_at ?? e.updatedAt ?? "",
    reviewDetailId: e.review_detail_id ?? e.reviewDetailId ?? null,
    altText: e.alt_text ?? e.altText ?? "",
    altTextGeneratedBy,
    aiGeneratedAltText,
    tagApplied: e.tag_applied ?? e.tagApplied ?? "",
    reviewLevelId: e.review_level_id ?? e.reviewLevelId ?? null,
    coverPhoto: e.cover_photo ?? e.coverPhoto ?? 0,
    sortOrder: e.sort_order ?? e.sortOrder ?? 0,
    width: e.width,
    height: e.height,
    widthThumb,
    heightThumb,
    imageUpdatedAt,
    othersUpdatedAt,
    tag_name: e.tag_name,
    alt_text: e.alt_text ?? e.altText ?? "",
    tag_applied: e.tag_applied ?? e.tagApplied ?? "",
    sort_order: e.sort_order ?? e.sortOrder,
    created_at: e.created_at ?? e.createdAt,
    updated_at: e.updated_at ?? e.updatedAt,
    user_id: e.user_id ?? e.userId,
    user_name: userName,
    business_id: e.business_id ?? e.businessId,
    path_thumb: pathThumb,
    type_id: e.type_id ?? e.typeId,
    review_id: e.review_id ?? e.reviewId,
    review_detail_id: e.review_detail_id ?? e.reviewDetailId,
    review_level_id: e.review_level_id ?? e.reviewLevelId,
    cover_photo: e.cover_photo ?? e.coverPhoto,
    width_thumb: widthThumb,
    height_thumb: heightThumb,
    alt_text_generated_by: altTextGeneratedBy,
    ai_generated_alt_text: aiGeneratedAltText,
    image_updated_at: imageUpdatedAt,
    others_updated_at: othersUpdatedAt,
    user_roles: e.user_roles || {
      isGeneralUser: false,
    },
    approved_for_public: Boolean(e.approved_for_public),
  };
}

export function mapAccessibilityTag(
  categpries: any[],
): AccessibilityCategory[] {
  return categpries.map((category) => ({
    catId: category.cat_id,
    catName: category.cat_name,
    catImage: category.cat_image,
    catImageDark: category.cat_image_selected,
    catType: category.cat_type,
    cat_display_sections: category.cat_display_sections,
    tags: category?.tags?.map((tag: any) => ({
      qtagsAll: tag.qtagsAll,
      qtags: tag.qtags.map(mapTag),
      reviewPhotos: (tag.reviewPhotos ?? []).map(mapReviewPhotoFromApi),
      latestReviewDetailUpdatedDate: tag.latestReviewDetailUpdatedDate,
    })),
  }));
}

export function mapHybridAccessibilityTag(
  categpries: any[],
): AccessibilityCategoryHybrid[] {
  return categpries.map((category) => ({
    catId: category.id,
    catName: category.name,
    catImage: category.image,
    catImageDark: category.image_selected,
    catType: category.category_type,
    categoryOptions: category.categoryOptions,
  }));
}

function mapTag(tag: any): AccessibilityTag {
  return {
    categoryId: tag.category_id,
    categoryDisplaySectionId: tag.categoryDisplaySectionId,
    categoryDisplaySectionName: tag.categoryDisplaySectionName,
    questionId: tag.question_id,
    tagId: tag.tag_id,
    tagName: tag.tag_name,
    tagCount: tag.tag_count,
    tagNote: tag.tag_note,
    tagOwnerNote: tag.tag_owner_note,
    tagHash: tag.tag_hash,
    reviewByUsername: tag.review_by_username,
    reviewByUserid: tag.review_by_userid,
    tag_image: tag.tag_image,
    tag_image_dark: tag.tag_image_dark,
    tag_image_selected: tag.tag_image_selected,
    tag_image_selected_dark: tag.tag_image_selected_dark,
    user_roles: tag.user_roles || {
      isGeneralUser: false,
    },
    approved_for_public: Boolean(tag.approved_for_public),
  };
}

function mapVenueAccessibilityExperienceStats(
  stats: any,
): VenueAccessibilityExperienceStats | undefined {
  if (!stats) {
    return undefined;
  }
  return {
    coolCount: stats.cool_count ?? 0,
    coolPercentage: stats.cool_percentage ?? 0,
    totalCount: stats.total_count ?? 0,
    weirdCount: stats.weird_count ?? 0,
    weirdPercentage: stats.weird_percentage ?? 0,
  };
}

export function mapVenueReviews(reviews: any): VenueReview[] {
  const venueReviews: VenueReview[] =
    reviews?.reviews?.map((review: any) => ({
      totalReviewDetails: review.total_review_details,
      likesCount: review.likesCount,
      dislikesCount: review.dislikesCount,
      flaggedReviews: review.flagged_reviews,
      categoryName: review.categoryName,
      id: review.id,
      userId: review.user_id,
      businessId: review.business_id,
      notes: review.notes,
      status: review.status,
      deletedAt: review.deleted_at,
      createdAt: review.created_at,
      updatedAt: review.updated_at,
      ownerNotes: review.owner_notes,
      reviewType: review.review_type,
      adminOverride: review.admin_override,
      adminUserId: review.admin_user_id,
      adminOverrideAt: review.admin_override_at,
      timeAgo: review.timeAgo,
      userDisabilityVisible: review.user_disability_visible,
      userFirstName: review.user_first_name,
      userLastName: review.user_last_name,
      userProfilePhoto: review.user_profile_photo,
      userProfileColorCode: review.user_profile_color_code,
      userAbility: review.user_ability,
      user_disability_visible: review.user_disability_visible,
      accessibility_experience: review.accessibility_experience,

      user: review.user,
      reviewPhotos:
        review.review_photos?.map((e: any) => mapReviewPhotoFromApi(e)) || [],
    })) || [];
  return venueReviews;
}

export async function addReview(
  venueId: string,
  notes: string,
  imageData: AddPhotoImageData[],
  accessibilityExperience: string,
  token: string,
) {
  try {
    const formData = new FormData();
    formData.append("business_id", venueId);
    formData.append("photoLength", imageData.length.toString());
    formData.append("notes", notes);
    formData.append("accessibility_experience", accessibilityExperience);

    for (let i = 0; i < imageData.length; i++) {
      const imageUri = imageData[i].imageUrl; // Assuming you want to send the first image
      const fileName = `image${i}.jpg`; // Adjust accordingly

      const file = {
        uri: imageUri,
        type: "image/jpeg",
        name: fileName,
      };
      formData.append(`image_${i}`, file as any, fileName);
    }

    const response = await fetchWithToken(
      `${apiServer + "/review/store-review-multiple-photo"}`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      },
    );
    const data = await response?.json();
    if (data.status === "error") {
      console.log(data);
      return data.message;
    } else {
      return data.data;
    }
  } catch (error: any) {
    console.log(error);
    return error.message;
  }
}

export async function addReviewDesktop(
  venueId: string,
  notes: string,
  imageData: AddPhotoImageData[],
  accessibilityExperience: string,
  token: string,
) {
  const formData = new FormData();
  formData.append("business_id", venueId);
  formData.append("photoLength", imageData.length.toString());
  formData.append("notes", notes);
  formData.append("accessibility_experience", accessibilityExperience);

  const fieldKeys: string[] = [];
  for (let i = 0; i < imageData.length; i++) {
    const blob = await (await fetch(imageData[i].imageUrl)).blob();
    formData.append(`image_${i}`, blob, `image${i}.jpg`);
    formData.append(`tag_applied_${i}`, imageData[i].selectedOption);
    formData.append(`alt_text_${i}`, imageData[i].note);
    formData.append(`notes_${i}`, imageData[i].note);
    fieldKeys.push(`image_${i}`);
  }

  const response = await fetchWithToken(
    `${apiServer}/review/store-review-multiple-photo`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    },
  );

  return resolveGuardrail(response, fieldKeys);
}

export async function syncPhotos({
  venueId,
  sortBy,
}: {
  venueId: string;
  sortBy: string;
}) {
  const formData = new FormData();
  formData.append("business_id", venueId);
  formData.append("sort_by", sortBy);
  const response = await fetch(`${apiServer + "/business/sync-photos"}`, {
    method: "POST",
    body: formData,
  });
  const data = await response.json();
  if (data.status === "error") {
    throw Error(data.message);
  } else {
    return data;
  }
}

export async function syncReviews({
  venueId,
  sortBy,
}: {
  venueId: string;
  sortBy: string;
}) {
  const formData = new FormData();
  formData.append("business_id", venueId);
  formData.append("sort_by", sortBy);
  const response = await fetch(`${apiServer + "/business/sync-reviews"}`, {
    method: "POST",
    body: formData,
  });
  const data = await response.json();
  if (data.status === "error") {
    throw Error(data.message);
  } else {
    return mapVenueReviews(data.data);
  }
}

export async function syncAccessibilityEnterprise(
  businessId: number,
  slug: string,
  options?: { password?: string; passwordToken?: string; ssoToken?: string },
): Promise<{
  accesibilityCategoriesEnterprise: ComplexCategoriesData[];
  floorTypeOptions: { id: number; title: string }[];
  enterpriseBusinessNotesActions: BusinessNoteAction[];
  businessNotesTasks: {
    Custom: BusinessNoteTask[];
    General: BusinessNoteTask[];
  };
  review_tags: ComplexCategoriesData[];
  businessNotesAreaLocationTypes: string[];
  businessNotesForMappers: BusinessNote[];
  businessNotesAll: businessNotesAll[];
  accessibility_filters: AccessibilityFilters;
  canViewPage: number;
  passwordProtectedPageToken?: string;
  isSsoProtect?: number;
  ssoTenantId?: string;
}> {
  const formData = new FormData();
  formData.append("business_id", businessId.toString());
  formData.append("slug", slug);
  if (options?.password) {
    formData.append("password", options.password);
  }

  // SSO JWT and password token both use the Authorization header.
  // SSO takes priority — if both are somehow present, prefer the SSO token.
  const headers: Record<string, string> = {};
  if (options?.ssoToken) {
    headers["Authorization"] = options.ssoToken;
  } else if (options?.passwordToken) {
    headers["Authorization"] = `Bearer ${options.passwordToken}`;
  }

  const response = await fetch(`${apiServer + "/business/sync-spaces"}`, {
    method: "POST",
    headers,
    body: formData,
  });
  const data = await response.json();
  if (data.status === "error") {
    throw Error(data.message);
  } else {
    return {
      accesibilityCategoriesEnterprise: (data.data?.review_tags ||
        []) as ComplexCategoriesData[],
      floorTypeOptions: data.data?.floorTypeOptions || [],
      enterpriseBusinessNotesActions: data.data?.businessNotesActions || [],
      businessNotesTasks: data.data?.businessNotesTasks || [],
      review_tags: data.data?.review_tags || [],
      businessNotesAreaLocationTypes:
        data.data?.businessNotesAreaLocationTypes || [],
      businessNotesForMappers: data.data?.businessNotesForMappers || [],
      businessNotesAll: data.data?.businessNotesAll || [],
      accessibility_filters: data.data?.accessibility_filters || [],
      canViewPage: data.data?.can_view_page ?? 1,
      passwordProtectedPageToken:
        data.data?.password_protected_page_token || undefined,
      isSsoProtect: data.data?.is_sso_protect,
      ssoTenantId: data.data?.sso_tenant_id,
    };
  }
}
